import {Component, ElementRef, inject, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {Store} from "@ngrx/store";
import {GeneralPartnerListActions, GeneralPartnerOverviewActions} from "../../store/fund.actions";
import {combineLatest, filter, skip, Subject, Subscription, take} from "rxjs";
import {debounceTime, distinctUntilChanged} from "rxjs/operators";
import {
    selectGeneralPartnerOverviewContent,
    selectGeneralPartnerOverviewLoading,
    selectGeneralPartnerOverviewParams,
    selectGeneralPartnerOverviewTotalElements
} from "../../store/general-partner/general-partner.selectors";
import {CodeTableEnum} from "../../../shared/model/code";
import {MatDialog} from "@angular/material/dialog";
import {GeneralPartnerNewDialogComponent} from "../../general-partner-new-dialog/general-partner-new-dialog.component";
import {MatCheckboxChange} from "@angular/material/checkbox";
import {Sort} from "@angular/material/sort";
import {PageEvent} from "@angular/material/paginator";
import {Router} from "@angular/router";
import {selectSettingsLoaded, selectTableColumns, selectTablePageSize, selectTableSort} from "../../../shared/user-setting/store/user-setting.selectors";
import {UserSettingActions} from "../../../shared/user-setting/store/user-setting.actions";
import {TableSettingsDialogComponent, TableSettingsDialogData, TableSettingsDialogResult} from "../../../shared/user-setting/table-settings-dialog/table-settings-dialog.component";

import {CommentCountsActions} from "../../../comment/store/comment.actions";
import {selectSelectedSourceDataset} from "../../../import/store/import.selectors";

const TABLE_KEY = "general-partner-overview";
const COMMENT_CONTEXT = "/gp/overview/";

const ALL_COLUMN_GROUPS = [
    {label: "Main", cols: ["gpId", "name", "quality", "funds"]},
];

const COLUMN_LABELS: Record<string, string> = {
    gpId: "ID",
    name: "General Partner",
    quality: "Quality",
    funds: "Funds",
};

const SORTABLE_COLUMNS = [
    {value: "id", label: "ID"},
    {value: "name", label: "General Partner"},
    {value: "quality", label: "Quality"},
];

@Component({
    selector: "valumize-general-partner-overview-page",
    templateUrl: "./general-partner-overview-page.component.html",
    styleUrls: ["./general-partner-overview-page.component.scss"],
    standalone: false,
    host: {class: "overview-page"}
})
export class GeneralPartnerOverviewPageComponent implements OnInit, OnDestroy {
    private static readonly ALL_VALID_COLUMNS = ALL_COLUMN_GROUPS.flatMap(g => g.cols);

    private readonly store = inject(Store);
    private readonly dialog = inject(MatDialog);
    private readonly router = inject(Router);

    private readonly searchSubject = new Subject<string>();
    private searchSubscription?: Subscription;
    private readonly settingsSubscription?: Subscription;
    private columnsSubscription?: Subscription;
    private dialogSubscription?: Subscription;
    private commentCountsSubscription?: Subscription;

    @ViewChild("searchInput") searchInput?: ElementRef<HTMLInputElement>;

    columns = ["gpId", "name", "quality", "funds"];
    codeTableGpQuality = CodeTableEnum.FUND_GENERALPARTNERQUALITY;
    searchVisible = false;
    showArchived = false;

    generalPartners$ = this.store.select(selectGeneralPartnerOverviewContent);
    totalElements$ = this.store.select(selectGeneralPartnerOverviewTotalElements);
    params$ = this.store.select(selectGeneralPartnerOverviewParams);
    isLoading$ = this.store.select(selectGeneralPartnerOverviewLoading);

    sortActive = "name";
    sortDirection: "asc" | "desc" | "" = "asc";

    constructor() {
        this.store.dispatch(GeneralPartnerOverviewActions.resetSearch());

        this.settingsSubscription = this.store.select(selectSettingsLoaded).pipe(
            filter(Boolean),
            take(1)
        ).subscribe(() => {
            this.store.dispatch(GeneralPartnerListActions.load({archived: false}));
            this.applySettingsFromStore();
        });
    }

    ngOnInit() {
        this.searchSubscription = this.searchSubject.pipe(
            debounceTime(300),
            distinctUntilChanged()
        ).subscribe(search => {
            this.store.dispatch(GeneralPartnerOverviewActions.changeSearch({search}));
        });

        this.columnsSubscription = this.store.select(selectTableColumns(TABLE_KEY)).pipe(
            skip(1),
            filter((cols): cols is string[] => !!cols)
        ).subscribe(cols => {
            const valid = this.filterValidColumns(cols);
            if (valid) this.columns = valid;
        });

        // Page-level comment indicator: load counts for this page's context, refreshed on dataset switch.
        this.commentCountsSubscription = this.store.select(selectSelectedSourceDataset).pipe(
            distinctUntilChanged()
        ).subscribe(() => this.store.dispatch(CommentCountsActions.load({inContext: COMMENT_CONTEXT})));
    }

    onSearchInput(event: Event) {
        const value = (event.target as HTMLInputElement).value;
        this.searchSubject.next(value);
    }

    onSortChange(sort: Sort) {
        if (sort.direction) {
            this.sortActive = sort.active;
            this.sortDirection = sort.direction;
        } else {
            this.sortActive = "name";
            this.sortDirection = "asc";
        }
        const sortString = `${this.sortActive},${this.sortDirection}`;
        this.store.dispatch(GeneralPartnerOverviewActions.changeSort({sort: sortString}));
    }

    onPageChange(event: PageEvent) {
        this.store.dispatch(GeneralPartnerOverviewActions.changePage({page: event.pageIndex, size: event.pageSize}));
    }

    toggleSearch() {
        this.searchVisible = !this.searchVisible;
        if (this.searchVisible) {
            setTimeout(() => this.searchInput?.nativeElement.focus());
        } else {
            this.searchSubject.next("");
        }
    }

    onArchivedChanged(event: MatCheckboxChange) {
        this.showArchived = event.checked;
        this.store.dispatch(GeneralPartnerOverviewActions.changeArchived({isArchived: this.showArchived}));
    }

    openTableSettings() {
        this.store.select(selectGeneralPartnerOverviewParams).pipe(take(1)).subscribe(params => {
            const sortParts = params.sort.split(",");
            const currentSort = {field: sortParts[0] || "name", direction: sortParts[1] === "desc" ? "desc" : "asc"} as const;

            const dialogData: TableSettingsDialogData = {
                tableKey: TABLE_KEY,
                columnGroups: ALL_COLUMN_GROUPS,
                columnLabels: COLUMN_LABELS,
                currentColumns: this.columns,
                currentPageSize: params.size,
                currentSort,
                pageSizeOptions: [10, 20, 30, 50, 100],
                sortableColumns: SORTABLE_COLUMNS,
                isAdmin: false,
                defaultColumns: GeneralPartnerOverviewPageComponent.ALL_VALID_COLUMNS,
                defaultPageSize: 20,
                defaultSort: {field: "name", direction: "asc"},
            };

            this.dialogSubscription = this.dialog.open(TableSettingsDialogComponent, {data: dialogData, width: "520px"})
                .afterClosed()
                .subscribe((result: TableSettingsDialogResult) => this.handleDialogResult(result));
        });
    }

    navigateToGP(gpId: number) {
        this.router.navigate(["/gp", gpId]);
    }

    openNewGeneralPartnerDialog() {
        this.dialog.open(GeneralPartnerNewDialogComponent);
    }

    ngOnDestroy() {
        this.searchSubscription?.unsubscribe();
        this.settingsSubscription?.unsubscribe();
        this.columnsSubscription?.unsubscribe();
        this.dialogSubscription?.unsubscribe();
        this.commentCountsSubscription?.unsubscribe();
    }

    private applySettingsFromStore() {
        combineLatest([
            this.store.select(selectTableColumns(TABLE_KEY)),
            this.store.select(selectTablePageSize(TABLE_KEY)),
            this.store.select(selectTableSort(TABLE_KEY))
        ]).pipe(take(1)).subscribe(([columns, pageSize, sort]) => {
            if (columns) {
                const valid = this.filterValidColumns(columns);
                if (valid) this.columns = valid;
            }
            if (sort) {
                this.sortActive = sort.field;
                this.sortDirection = sort.direction as "asc" | "desc";
            }
            this.store.dispatch(GeneralPartnerOverviewActions.applySettings({
                size: pageSize ?? undefined,
                sort: sort ? `${sort.field},${sort.direction}` : undefined
            }));
            this.store.dispatch(GeneralPartnerOverviewActions.search());
        });
    }

    private filterValidColumns(columns: string[]): string[] | null {
        const valid = columns.filter(c => GeneralPartnerOverviewPageComponent.ALL_VALID_COLUMNS.includes(c));
        return valid.length > 0 ? valid : null;
    }

    private handleDialogResult(result: TableSettingsDialogResult) {
        if (!result) return;

        this.columns = result.columns;
        this.sortActive = result.sort.field;
        this.sortDirection = result.sort.direction as "asc" | "desc";
        this.store.dispatch(GeneralPartnerOverviewActions.applySettings({
            size: result.pageSize,
            sort: `${result.sort.field},${result.sort.direction}`
        }));
        this.store.dispatch(GeneralPartnerOverviewActions.search());

        const payload = {tableKey: TABLE_KEY, columns: result.columns, pageSize: result.pageSize, sort: result.sort};

        if (result.saveAdmin) {
            this.store.dispatch(UserSettingActions.saveAdminTableSettings(payload));
        }
        if (result.saveUser) {
            this.store.dispatch(UserSettingActions.saveTableSettings(payload));
        }
        if (!result.saveUser && !result.saveAdmin) {
            this.store.dispatch(UserSettingActions.applyTableSettings(payload));
        }
    }
}
